import { useState, useEffect, useRef } from "react";

const products = [
  { name: "Garments" },
  { name: "Beauty and Health Care Products" },
  { name: "Mobile Accessories and Mobiles" },
  { name: "Rolled Gold and Fancy Items" },
  { name: "Herbal, Ayurvedic, and Allopathic Products" },
  { name: "Electronics - LED TVs, ACs, and Washing Machines" },
  { name: "Home Need Products" },
  { name: "EV Scooters" },
  { name: "Gold and Silver (Coming Soon)" },
];

function useInView() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );

    const el = ref.current;

    if (el) obs.observe(el);

    return () => {
      if (el) obs.unobserve(el);
    };
  }, []);

  return [ref, visible];
}

function Reveal({
  children,
  delay = 0,
  style = {},
  className = "",
}) {
  const [ref, visible] = useInView();

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible
          ? "translateY(0px)"
          : "translateY(15px)",
        transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s,
        transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export default function AboutUs() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .about-page {
          background-color: #ffffff;
          color: #1a1a1a;
          font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
          line-height: 1.6;
          padding-bottom: 80px;
        }

        .container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Top Header */
        .top-bar {
          height: 5px;
          background: #006600;
          width: 100%;
          margin-bottom: 60px;
        }

        .header-section {
          text-align: center;
          margin-bottom: 60px;
        }

        .main-title {
          font-size: clamp(32px, 5vw, 48px);
          font-weight: 800;
          color: #111;
          margin-bottom: 16px;
          letter-spacing: -1px;
        }

        .sub-header {
          font-size: 18px;
          font-weight: 600;
          color: #444;
          margin-bottom: 40px;
        }

        /* Content Sections */
        .content-block {
          margin-bottom: 30px;
          font-size: 16px;
          color: #333;
          text-align: justify;
        }

        .content-block p {
          margin-bottom: 24px;
          line-height: 1.8;
        }

        .highlight {
          font-weight: 700;
          color: #000;
        }

        /* Founder Section */
        .founder-card {
          margin: 40px 0;
          padding: 35px;
          border-radius: 24px;
          background: linear-gradient(
            135deg,
            #f7faf7 0%,
            #ffffff 100%
          );
          border: 1px solid #e5eee5;
          box-shadow: 0 12px 35px rgba(0, 70, 0, 0.06);
          position: relative;
          overflow: hidden;
        }

        .founder-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 5px;
          height: 100%;
          background: #006600;
        }

        .founder-label {
          display: inline-block;
          font-size: 13px;
          font-weight: 700;
          color: #006600;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: 8px;
        }

        .founder-name {
          font-size: 30px;
          font-weight: 800;
          color: #111;
          margin: 0 0 5px;
        }

        .founder-role {
          font-size: 16px;
          font-weight: 700;
          color: #006600;
          margin-bottom: 20px;
        }

        .founder-card p {
          color: #444;
          font-size: 15.5px;
          line-height: 1.8;
          margin-bottom: 16px;
        }

        .founder-card p:last-child {
          margin-bottom: 0;
        }

        /* Categories */
        .categories-section {
          margin: 40px 0;
        }

        .categories-title {
          font-weight: 700;
          margin-bottom: 20px;
        }

        .categories-list {
          list-style: none;
          padding-left: 20px;
        }

        .categories-list li {
          position: relative;
          margin-bottom: 12px;
          padding-left: 20px;
          font-weight: 500;
        }

        .categories-list li::before {
          content: "•";
          position: absolute;
          left: 0;
          color: #006600;
          font-weight: bold;
        }

        /* Cards Section */
        .info-section {
          margin-top: 80px;
          display: flex;
          flex-direction: column;
          gap: 40px;
        }

        .info-card.full {
          width: 100%;
        }

        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
        }

        .info-card {
          padding: 20px 0;
        }

        .info-card h3 {
          font-size: 20px;
          font-weight: 800;
          margin-bottom: 16px;
          color: #111;
        }

        .info-card p,
        .info-card ul {
          font-size: 15px;
          color: #444;
          line-height: 1.7;
        }

        .info-card ul {
          list-style: none;
          padding: 0;
        }

        .info-card ul li {
          margin-bottom: 8px;
        }

        /* Map Section */
        .visit-section {
          margin-top: 80px;
          border-top: 1px solid #eee;
          padding-top: 60px;
        }

        .map-wrapper {
          width: 100%;
          height: 400px;
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid #eee;
          margin-top: 30px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .map-wrapper iframe {
          width: 100%;
          height: 100%;
          border: 0;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .main-title {
            font-size: 32px;
          }

          .info-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }

          .sub-header {
            font-size: 16px;
          }

          .map-wrapper {
            height: 300px;
          }

          .founder-card {
            padding: 25px;
          }

          .founder-name {
            font-size: 25px;
          }
        }
      `}</style>

      <div className="about-page">
        <div className="top-bar" />

        <div className="container">

          {/* Header */}
          <Reveal>
            <div className="header-section">

              <h1 className="main-title">
                SUN DISTRIBUTORS
              </h1>

              <p
                className="sub-header"
                style={{ marginBottom: "10px" }}
              >
                Change Your Thought, Change Your Life
              </p>

              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#666",
                  marginBottom: "40px",
                }}
              >
                Founded by{" "}
                <strong>R. Janaki Ram</strong>{" "}
                in Visakhapatnam
              </div>

            </div>
          </Reveal>

          {/* Main Content */}
          <div className="content-block">

            {/* Company Introduction */}
            <Reveal delay={0.1}>
              <p>
                <span className="highlight">
                  SUN DISTRIBUTORS Manufacturing Company
                </span>{" "}
                was founded by{" "}
                <strong>Mr. R. Janaki Ram</strong>,
                an experienced Trader and Entrepreneur from
                Visakhapatnam, Andhra Pradesh, India.
              </p>
            </Reveal>

            {/* Founder Section */}
            <Reveal delay={0.15}>
              <div className="founder-card">

                <span className="founder-label">
                  Founder & Business Leader
                </span>

                <h2 className="founder-name">
                  R. Janaki Ram
                </h2>

                <div className="founder-role">
                  Trader | Entrepreneur | Business Development
                </div>

                <p>
                  <strong>R. Janaki Ram</strong> is an experienced
                  Trader and Entrepreneur with a strong focus on
                  product sourcing, trading, distribution, and
                  business development.
                </p>

                <p>
                  With practical experience in the trading and
                  distribution sector, his vision is to build a
                  reliable business platform that connects quality
                  products with customers while creating meaningful
                  opportunities for distributors and business
                  partners.
                </p>

                <p>
                  His business approach focuses on{" "}
                  <strong>
                    trust, quality, transparency, innovation,
                    and long-term relationships
                  </strong>.
                  Through SUN DISTRIBUTORS, his goal is to develop
                  a professional distribution network and support
                  individuals who want to build and grow their own
                  businesses.
                </p>

              </div>
            </Reveal>

            {/* Vision */}
            <Reveal delay={0.2}>
              <p>
                Guided by the powerful vision —{" "}
                <span className="highlight">
                  "Change Your Thought, Change Your Life With{" "}
                  <strong>SUN DISTRIBUTORS</strong>"
                </span>{" "}
                — SUN DISTRIBUTORS was established to empower
                individuals to become financially independent
                distributors and create sustainable sources of
                income for themselves and their families.
              </p>
            </Reveal>

            {/* Purpose */}
            <Reveal delay={0.25}>
              <p>
                From its beginning, SUN DISTRIBUTORS has aimed to
                help every individual — including youth, homemakers,
                RMP doctors, tailors, and retired employees — to
                start their own business with confidence, skill,
                and purpose.
              </p>
            </Reveal>

            {/* Guidance */}
            <Reveal delay={0.3}>
              <p>
                The company provides complete guidance, practical
                training, and a proven system to help individuals
                start and develop their business through its
                franchise model.
              </p>
            </Reveal>

            {/* Product Categories */}
            <Reveal delay={0.35}>
              <p>
                The core business activities of SUN DISTRIBUTORS
                include manufacturing, wholesaling, and marketing
                of high-quality products across nine major
                categories:
              </p>
            </Reveal>

            <Reveal delay={0.4}>
              <div className="categories-section">

                <ul className="categories-list">
                  {products.map((p, i) => (
                    <li key={i}>
                      {p.name}
                    </li>
                  ))}
                </ul>

              </div>
            </Reveal>

            {/* Products */}
            <Reveal delay={0.45}>
              <p>
                Each product category is designed to meet the
                everyday needs of customers while offering
                business opportunities for franchise distributors.
              </p>
            </Reveal>

            {/* Franchise */}
            <Reveal delay={0.5}>
              <p>
                SUN DISTRIBUTORS operates through a franchise
                business model, providing franchise partners with
                the opportunity to promote, sell, and distribute{" "}
                <span className="highlight">
                  SUN DISTRIBUTORS
                </span>{" "}
                products under its brand.
              </p>
            </Reveal>

            {/* Support */}
            <Reveal delay={0.55}>
              <p>
                Franchise partners receive complete support —
                including training programs, product knowledge,
                marketing materials, and operational guidance —
                to build and develop their business.
              </p>
            </Reveal>

            {/* Expansion */}
            <Reveal delay={0.6}>
              <p>
                Headquartered in Visakhapatnam, Andhra Pradesh,
                SUN DISTRIBUTORS is expanding across Andhra Pradesh,
                Telangana, Tamil Nadu, Karnataka, and Odisha, with
                the vision of building a strong network of
                independent distributors across India.
              </p>
            </Reveal>

            {/* Distributor Development */}
            <Reveal delay={0.65}>
              <p>
                Through its Distributor Development Program,
                SUN DISTRIBUTORS focuses on helping individuals
                understand product markets, develop business
                skills, and build their distribution networks.
              </p>
            </Reveal>

            {/* Income Statement */}
            <Reveal delay={0.7}>
              <p>
                Many participants have the opportunity to develop
                consistent earnings by following the SUN
                DISTRIBUTORS business system and applying the
                company's training and distribution strategies.
              </p>
            </Reveal>

            {/* Values */}
            <Reveal delay={0.75}>
              <p>
                SUN DISTRIBUTORS stands for{" "}
                <strong>
                  trust, quality, opportunity, and empowerment.
                </strong>
              </p>
            </Reveal>

            {/* Company Message */}
            <Reveal delay={0.8}>
              <p>
                It is more than a company — it is a business
                platform dedicated to helping individuals develop
                their entrepreneurial skills, become successful
                distributors, and work toward financially
                independent lives.
              </p>
            </Reveal>

            {/* Closing */}
            <Reveal delay={0.85}>
              <p>
                With continuous innovation, quality products, and
                a people-focused mission, SUN DISTRIBUTORS
                continues to build a growing community of
                distributors and business partners.
              </p>
            </Reveal>

            <Reveal delay={0.9}>
              <p className="highlight">
                Change Your Thought, Change Your Life With{" "}
                <strong>SUN DISTRIBUTORS</strong>
              </p>

              <p>
                Distributors Manufacturing Company
              </p>
            </Reveal>

          </div>

          {/* Information Section */}
          <div className="info-section">

            {/* Why Choose Us */}
            <Reveal delay={0.95}>
              <div className="info-card full">

                <h3>
                  Why Choose Us
                </h3>

                <ul>
                  <li>
                    • Pioneering local manufacturing initiatives
                  </li>

                  <li>
                    • Empowering aspiring distributors
                  </li>

                  <li>
                    • Commitment to sustainability and innovation
                  </li>

                  <li>
                    • Professional product distribution support
                  </li>

                  <li>
                    • Customer-focused business approach
                  </li>

                  <li>
                    • Long-term business development opportunities
                  </li>
                </ul>

              </div>
            </Reveal>

            {/* Mission & Vision */}
            <div className="info-grid">

              <Reveal delay={1.0}>
                <div className="info-card">

                  <h3>
                    Our Mission
                  </h3>

                  <p>
                    To guide, train, and support individuals from
                    all walks of life to start and develop their
                    own business through quality products,
                    professional distribution support, and
                    practical business education.
                  </p>

                </div>
              </Reveal>

              <Reveal delay={1.1}>
                <div className="info-card">

                  <h3>
                    Our Vision
                  </h3>

                  <p>
                    To create a strong network of successful
                    distributors through business education,
                    mentorship, quality products, and sustainable
                    distribution opportunities.
                  </p>

                </div>
              </Reveal>

            </div>

          </div>

          {/* Office / Map */}
          <div className="visit-section">

            <Reveal delay={0.2}>

              <h2
                className="main-title"
                style={{
                  textAlign: "center",
                  fontSize: "32px",
                }}
              >
                Visit Our Office
              </h2>

              <p
                style={{
                  textAlign: "center",
                  color: "#666",
                  maxWidth: "600px",
                  margin: "0 auto 20px",
                }}
              >
                F. NO 101, 48-13-17, Sringar,
                Janakirama street, Near RTC Complex,
                Visakhapatnam-16
              </p>

              <div className="map-wrapper">

                <a
                  href="https://www.google.com/maps/search/?api=1&query=F.+NO+101,+48-13-17,+Sringar,+Janakirama+street,+Near+RTC+Complex,+Visakhapatnam-16"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "block",
                    height: "100%",
                    cursor: "pointer",
                  }}
                  title="Click to view on Google Maps"
                >

                  <iframe
                    title="SUN DISTRIBUTORS Location"
                    src="https://maps.google.com/maps?q=F.%20NO%20101,%2048-13-17,%20Sringar,%20Janakirama%20street,%20Near%20RTC%20Complex,%20Visakhapatnam-16&t=&z=16&ie=UTF8&iwloc=&output=embed"
                    allowFullScreen
                    loading="lazy"
                    style={{
                      pointerEvents: "none",
                    }}
                  ></iframe>

                </a>

              </div>

            </Reveal>

          </div>

        </div>
      </div>
    </>
  );
}